import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { logError } from '@office-start/telemetry-service';
import { BehaviorSubject, Observable, SubscriptionLike } from 'rxjs';
import { distinctUntilChanged, first, map, pairwise, skip, take } from 'rxjs/operators';

import { OfficeRoute } from '../constants/office-route.enum';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FileItem, FileType } from '../models/file-item.model';
import { FileUrlModel } from '../models/file-url.model';
import { ApplicationUrlParam, INewFileTemplateSource, NewFileInfo } from '../models/jsapi.model';
import { WdOriginType } from '../models/telemetry-constants.model';
import { BootAndLoadInfo, BootNewDocumentUiHostFunction, DocumentInfo, DocumentQueryParams, UiHostRouteFunction, WopiFetchData } from '../models/wac-host.model';
import { MruFileItem } from '../mru/mru-file-item.model';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { ContextService } from '../shared-context/context.service';
import { ReactShellConfigService } from '../shared-context/react-shell-config.service';
import { getDeferredPromise } from '../utilities/promise-deffered.utility';
import { UrlParser } from '../utilities/url-parser.utility';
import { createWacContentInstrumentationParams } from '../utilities/wac-content-utility';
import { DocumentService } from './document.service';
import { OfficeRouteService } from './office-route.service';
import { ShellShimService } from './shell-shim.service';
import { UiHostBootstrapperService } from './ui-host-bootstrapper.service';
import { WindowService } from './window.service';

export interface WacDocumentData {
    bootAndLoadInfo: BootAndLoadInfo;
    visible: boolean;
    bootCompleted: boolean;
    routeEndPromise?: Promise<void>;
}

export interface BootedWacDocumentData {
    bootId: number;
    data: Observable<WacDocumentData>;
}

@Injectable({
    providedIn: 'root'
})
export class WacContentService {
    public static readonly HiddenShellClass = 'hide-shell';
    // List of filetypes we want to send the control flight signal to
    public static readonly WacHostControlFileTypes = [FileType.Word, FileType.Excel, FileType.PowerPoint];

    // Use map to provide quick access to loaded documents for when expand past having one open at a time
    private readonly _loadedDocuments: BehaviorSubject<ReadonlyMap<number, BehaviorSubject<WacDocumentData>>>;
    private locationSubscription: SubscriptionLike;

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly contextService: ContextService,
        private readonly documentService: DocumentService,
        private readonly instrumentationService: InstrumentationService,
        private readonly location: Location,
        private readonly officeRouteService: OfficeRouteService,
        private readonly reactShellConfigService: ReactShellConfigService,
        private readonly shellShimService: ShellShimService,
        private readonly uiHostBootstrapperService : UiHostBootstrapperService,
        private readonly windowService: WindowService
    ) {
        this._loadedDocuments = new BehaviorSubject<ReadonlyMap<number, BehaviorSubject<WacDocumentData>>>(new Map<number, BehaviorSubject<WacDocumentData>>());
        // TODO: Change to observable if the wac host being enabled becomes a
        // changing value (flight=clientpwas)
        if (this.capabilitiesService.wacHostEnabled()) {
            this.setupAbandonListener();

            if (!this.capabilitiesService.capabilities.isEmbedMode) {
                // We don't have the shell in embed mode
                this.setupResetShellListener();
            }
        }
    }

    public static getParamsForDocumentInfo(documentInfo: DocumentInfo, additionalParams: Params = {}): Params {
        return this.getWacParams(documentInfo.driveId, documentInfo.docId, documentInfo.docUrl, additionalParams);
    }

    public static getParamsForMruFileItem(item: MruFileItem, additionalParams: Params = {}): Params {
        return this.getWacParams(item.driveId, item.docId, item.canonicalUrl, additionalParams);
    }

    public static getWacParams(driveId: string, itemId: string, docUrl: string, additionalParams: Params = {}): Params {
        return {
            ...additionalParams,
            [DocumentQueryParams.DriveId]: driveId,
            [DocumentQueryParams.ItemId]: itemId,
            [DocumentQueryParams.DocUrl]: docUrl
        };
    }

    public static getDocumentInfoForMruFileItem(item: MruFileItem, wdOrigin: string): DocumentInfo {
        return {
            docId: item.docId,
            docUrl: item.canonicalUrl,
            driveId: item.driveId,
            fileType: item.fileType,
            fileName: item.fileName,
            wdOrigin
        };
    }

    public static isValidDocumentInfo(documentInfo: DocumentInfo) {
        return (!!documentInfo.docId && !!documentInfo.driveId) || !!documentInfo.docUrl;
    }

    /**
     * Syntactic sugar for bootstrap and route that allows the caller to pass a mru file item
     * @param item mru file item to boot and load
     * @param wdOrigin The origin of this document (MRU, recommended, etc.)
     * @param additionalParams Additional params to be added to the content page url
     * @param clickTime Time the document was clicked
     * @param pendingMruItem Promise for mru file item inflation
     */
    public bootstrapAndRouteMruFileItem(item: MruFileItem, wdOrigin: string, additionalParams: Params = {}, clickTime: number, pendingMruItem?: Promise<MruFileItem>) {
        this.bootstrapAndRoute(
            WacContentService.getDocumentInfoForMruFileItem(item, wdOrigin),
            additionalParams,
            clickTime,
            pendingMruItem ? this.uiHostBootstrapperService.convertMruInflationPromise(pendingMruItem, wdOrigin, clickTime) : undefined
        );
    }

    /**
     *  Bootstraps, loads, and routes to a content page based on the given informantion
     * @param documentInfo Information about the document used to boot
     * @param additionalParams Additional params to be added to the content page url
     * @param clickTime Time the document was clicked
     * @param pendingData Promise for already in progress wopi fetch data call
     */
    public async bootstrapAndRoute(documentInfo: DocumentInfo, additionalParams: Params = {}, clickTime?: number, pendingData?: Promise<WopiFetchData>) {
        if (!this.capabilitiesService.wacHostEnabledForFileType(documentInfo.fileType)) {
            return;
        }

        const routeFunction = await this.bootstrap(documentInfo, clickTime, pendingData);
        routeFunction(additionalParams);
    }

    /**
     * Bootstraps a zero byte document and returns a function to complete the navigation after the document is fully created
     * @param documentInfo Document info of zero byte document
     * @param clickTime click time
     * @param createNewPromise Promise created in the create-document service
     * @returns Function to route to the booted document
     */
    public bootstrapZeroByteFile(documentInfo: DocumentInfo, clickTime: number, createNewPromise: Promise<FileUrlModel>, templateId?: string, templateDriveId?: string): BootNewDocumentUiHostFunction {
        if (!this.capabilitiesService.wacHostEnabledForFileType(documentInfo.fileType)) {
            return;
        }

        const documentInfoPromise: Promise<DocumentInfo> = createNewPromise.then(data => {
            const newDocumentInfo: DocumentInfo = {
                docId: data.docId,
                driveId: data.driveId,
                docUrl: data.canonicalUrl,
                fileName: data.fileName,
                wdOrigin: documentInfo.wdOrigin,
                fileType: documentInfo.fileType,
                newFile: true
            };

            const newFilePath = `${this.windowService.nativeWindow.location.origin}${this.getContentPath(documentInfo.fileType)}?${this.getQueryParamString(WacContentService.getParamsForDocumentInfo(newDocumentInfo))}`;
            const templateSource: INewFileTemplateSource | undefined = templateId ? {
                FileGetUrl: '',
                DocUniqueId: templateId
            } : undefined;

            const newFileInfo: NewFileInfo = {
                NewFilePath: newFilePath,
                TemplateSource: templateSource
            };

            newDocumentInfo.newFileInfo = newFileInfo;

            return newDocumentInfo;
        });

        const bootAndLoadInfo = this.uiHostBootstrapperService.bootstrapZeroByteDocument(documentInfo, clickTime, documentInfoPromise, templateId, templateDriveId);

        return async () => {
            // We don't actually start booting until we know a user doesn't have the client app installed
            this.logLoadStart(bootAndLoadInfo);
            const routeFunction = await this.updateBootedDocumentsMap(bootAndLoadInfo);
            routeFunction();

            bootAndLoadInfo.data.documentIdentifierPromise.then(newDocumentInfo => {
                this.updateInformation(bootAndLoadInfo.bootedDocumentInfo.bootId, newDocumentInfo);
            });
        };
    }

    /**
     * This function should be exclusively used by the wac-content-loader component
     * It has functionality to ignore load calls from that component.
     * @param documentInfo Document info used to boot and load the document
     * @param clickTime Time of click
     * @param firstLoad if this is the first load call from the loader component
     */
    public async bootstrapDocumentFromLoader(documentInfo: DocumentInfo, clickTime: number, firstLoad: boolean = false) {
        if (!this.capabilitiesService.wacHostEnabledForFileType(documentInfo.fileType)) {
            return;
        }

        const loadedDocuments = await this.hasLoadedDocumentsObservable().toPromise();
        if (firstLoad && loadedDocuments) {
            return;
        }

        const applicationParams = this.getApplicationParamsFromDocument(documentInfo);
        documentInfo.applicationUrlParams = applicationParams;

        const navId = documentInfo.applicationUrlParams[ApplicationUrlParam.Nav];
        if (navId && !this.activatedRoute?.snapshot?.queryParams?.[DocumentQueryParams.Nav]) {
            // Powerpoint currently doesn't accept the actnavid param in jsapi, but instead reads the 'nav' query param
            // from the current url
            this.officeRouteService.updateQueryParams({ nav: navId });
        }

        this.bootstrap(documentInfo, clickTime);
    }

    /**
     * Bootstraps and loads a mru item without navigating to a content page
     * @param item Mru item to boot and load
     * @param originType wdOrigin type of this document
     * @param preload Is the document should be loaded in the background or not
     * @return A function that will route to the loaded documents
     */
    public loadMruItem(item: MruFileItem, originType: WdOriginType, preload: boolean = false): Promise<UiHostRouteFunction> {
        return this.bootstrap(
            {
                docId: item.docId,
                driveId: item.driveId,
                docUrl: item.canonicalUrl,
                fileType: item.fileType,
                wdOrigin: this.contextService.getWdOrigin(originType)
            },
            Date.now(),
            undefined,
            preload
        );
    }

    public prebootDocument(item: MruFileItem, originType: WdOriginType) {
        this.uiHostBootstrapperService .prebootDocument(
            {
                docId: item.docId,
                driveId: item.driveId,
                docUrl: item.url,
                fileType: item.fileType,
                wdOrigin: this.contextService.getWdOrigin(originType)
            }
        );
    }

    /**
     * Bootstraps the document provided and loads a wac content component with the information without routing
     * @param documentInfo The document info to load
     * @param clickTime The click time to activate this flow
     * @param pendingData Promise of wopi data already being fetched
     * @param preload Boolean determining if the wac content component should be hidden on load
     * @return A function that will route to the loaded documents
     */
    public bootstrap(documentInfo: DocumentInfo, clickTime = Date.now(), pendingData?: Promise<WopiFetchData>, preload: boolean = false): Promise<UiHostRouteFunction> {
        if (!this.capabilitiesService.wacHostEnabledForFileType(documentInfo.fileType)) {
            return;
        }

        const bootAndLoadInfo = this.uiHostBootstrapperService.bootstrapItem(documentInfo, clickTime, pendingData);
        this.logLoadStart(bootAndLoadInfo);

        return this.updateBootedDocumentsMap(bootAndLoadInfo, preload);
    }

    public prebootApplication(fileType: FileType, wdOrigin: string) {
        if (fileType !== FileType.Visio) {
            return;
        }
        const app = this.uiHostBootstrapperService.bootstrapApplication(fileType, wdOrigin);
        if (!app) {
            return;
        }

        const bootId = app.correlationAndBoot[1];

        const data: WacDocumentData = {
            bootAndLoadInfo: app.loadData,
            visible: false,
            bootCompleted: false
        };

        // We are only allowing one document loaded at time for the moment
        const dataSubject = new BehaviorSubject(data);
        this._loadedDocuments.next(new Map<number, BehaviorSubject<WacDocumentData>>([[bootId, dataSubject]]));
    }

    /**
     * Updates the loadedDocuments observable with the booted document information
     * @param bootAndLoadInfo Boot information returned from the bootstrapper service
     * @param preload Boolean determining if the wac content component should be hidden on load
     * @return A function that will route to the loaded documents
     */
    public async updateBootedDocumentsMap(bootAndLoadInfo: BootAndLoadInfo, preload: boolean = false): Promise<UiHostRouteFunction> {
        const [routeEndPromise, resolveRouteEnd, rejectRouteEnd] = getDeferredPromise<void>();

        const currentDocs = await this.getCurrentLoadedDocuments();
        const currentBootedDocSubject = currentDocs.get(bootAndLoadInfo.bootedDocumentInfo.bootId);

        if (!currentBootedDocSubject) {

            const data: WacDocumentData = {
                bootAndLoadInfo,
                visible: !preload,
                bootCompleted: false,
                routeEndPromise
            };

            if (!this.locationSubscription || this.locationSubscription?.closed) {
                this.locationSubscription = this.location.subscribe(value => {
                    // Activated snapshot hasn't been updated when we are parsing browser location push/push events
                    // so we have to parse the url's location for the content data
                    const url = value.url ?? '';

                    const queryParams = UrlParser.getQueryParams(url);
                    const fileTypeName = this.getAppNameFromUrl(url);
                    const fileType = FileItem.fileTypeNameToFileType(fileTypeName);

                    const documentInfo = this.getDocumentInfoFromParams(queryParams, fileType);

                    if (!documentInfo || !WacContentService.isValidDocumentInfo(documentInfo)) {
                        return;
                    }
                    this.bootstrapDocumentFromLoader(documentInfo, Date.now(), false);
                });
            }

            // We are only allowing one document loaded at time for the moment
            const dataSubject = new BehaviorSubject(data);
            this._loadedDocuments.next(new Map<number, BehaviorSubject<WacDocumentData>>([[bootAndLoadInfo.bootedDocumentInfo.bootId, dataSubject]]));
        } else {
            // If we already have a document with this boot id, just update the information
            const bootedDocInfo = currentBootedDocSubject.getValue();
            currentBootedDocSubject.next({
                ...bootedDocInfo,
                visible: !preload,
                bootAndLoadInfo
            });
        }

        return (additionalParams = {}) => {
            this.routeToContentPage(bootAndLoadInfo.bootedDocumentInfo.documentInfo, additionalParams).then(finish => {
                if (finish) {
                    return resolveRouteEnd();
                }
                return rejectRouteEnd();
            });
        };
    }

    public updateInformation(bootId: number, documentInfo?: DocumentInfo, visible?: boolean, updateQueryParams = false) {
        const current = this._loadedDocuments.getValue()?.get(bootId);
        if (!current) {
            return;
        }

        const currentValue = current.getValue();
        if (!currentValue) {
            return;
        }

        const newData: WacDocumentData = {
            ...currentValue,
            bootAndLoadInfo: {
                correlationId: currentValue.bootAndLoadInfo.correlationId,
                bootedDocumentInfo: {
                    bootId,
                    documentInfo: documentInfo ?? currentValue.bootAndLoadInfo.bootedDocumentInfo.documentInfo
                },
                data: currentValue.bootAndLoadInfo.data
            },
            visible: visible ?? currentValue.visible
        };

        current.next(newData);

        if (updateQueryParams) {
            currentValue.routeEndPromise.then(() => {
                const params = WacContentService.getParamsForDocumentInfo(newData.bootAndLoadInfo.bootedDocumentInfo.documentInfo);
                this.officeRouteService.updateQueryParams(params);
            });
        }
    }

    public unloadAllDocuments() {
        this.locationSubscription.unsubscribe();
        this._loadedDocuments.next(new Map<number, BehaviorSubject<WacDocumentData>>());
    }

    public async resetShell() {
        if (this.capabilitiesService.capabilities.isEmbedMode) {
            // We don't have the shell in embed mode so this is a noop
            return;
        }
        const shellHeader: HTMLElement = this.documentService.nativeDocument.getElementById('ReactShellHeader');
        if (shellHeader && shellHeader.childElementCount) {
            this.documentService.removeCssClassFromElement(shellHeader, WacContentService.HiddenShellClass);
            const shim = await this.shellShimService.getShellShim();
            shim.Notifications.UnmuteToasts && shim.Notifications.UnmuteToasts();
        } else {
            // If we don't have a shell header and we aren't using the office.com shell on the wac content page
            // We have to bootstrap the react shell for the homepage
            const bootstrapUrl = this.reactShellConfigService.reactShellConfig.resourceUrl;
            if (bootstrapUrl) {
                this.documentService.addScriptToPage(
                    bootstrapUrl,
                    () => this.windowService.nativeWindow.bootstrapReactShell(),
                    () => {
                        const bootstrapParamObj = {
                            [InstrumentationParam.ShellBootAfterStartup]: 'true',
                            [InstrumentationParam.ResourceUrl]: bootstrapUrl
                        };
                        this.instrumentationService.logErrorEvent(KnownInstrumentationEvents.ShellBootstrapperError, bootstrapParamObj);

                        logError({
                            eventName: 'Shell_Bootstrapper_Error',
                            errorType: 'Shell_Bootstrap_Error',
                            message: 'Fail to add shell bootstrap script to page.',
                            debugInfo: JSON.stringify(bootstrapParamObj)
                        });
                    }
                );
            }
        }
    }

    public getLoadedDocumentsObservable(): Observable<ReadonlyArray<BootedWacDocumentData>> {
        return this._loadedDocuments.asObservable().pipe(map(docMap => Array.from(docMap.keys()).map(bootId => {
            const data = docMap.get(bootId);
            return {
                bootId,
                data: data.asObservable()
            };
        })));
    }

    public hasLoadedDocumentsObservable(): Observable<boolean> {
        return this._loadedDocuments.asObservable().pipe(map(docMap => docMap.size > 0), take(1));
    }

    public getCurrentLoadedDocuments(): Promise<ReadonlyMap<number, BehaviorSubject<WacDocumentData>>> {
        return this._loadedDocuments.asObservable().pipe(first()).toPromise();
    }

    public getDocumentInfoFromParams(params: Params, fileType: FileType, wdOrigin?: string): DocumentInfo {
        const docId = params[DocumentQueryParams.ItemId];
        const driveId = params[DocumentQueryParams.DriveId];
        const docUrl = this.decodeParamWithKey(params, DocumentQueryParams.DocUrl) ?? this.decodeParamWithKey(params, DocumentQueryParams.ContentUri);
        const navId = params[DocumentQueryParams.Nav];
        return {
            docId,
            driveId,
            docUrl,
            wdOrigin: wdOrigin ?? this.contextService.getWdOrigin(WdOriginType.Direct),
            applicationUrlParams: navId ? { [ApplicationUrlParam.Nav]: navId } : undefined,
            fileType
        };
    }

    public getWacHostControlQueryParams(fileType: FileType): Params {
        if (WacContentService.WacHostControlFileTypes.indexOf(fileType) >= 0 && this.capabilitiesService.capabilities.wacHostControlFlight) {
            if (this.capabilitiesService.capabilities.pwaEnabled) {
                return { wdwachostcf: 'hwa' };
            } else {
                return { wdwachostcf: 'web' };
            }
        }
        return {};
    }

    public async markBootCompleted(bootId: number) {
        const loadedMap = this._loadedDocuments.getValue();
        const dataSubject = loadedMap.get(bootId);

        const currentData = await dataSubject.asObservable().pipe(first()).toPromise();

        dataSubject.next({
            ...currentData,
            bootCompleted: true
        });
    }

    public getApplicationParamsFromDocument({
        docUrl
    }: DocumentInfo): Params {
        if (!docUrl) {
            return {};
        }

        const params = UrlParser.getQueryParams(docUrl);
        const navId = params ? params['nav'] : undefined;
        return !navId
            ? {}
            : { [ApplicationUrlParam.Nav]: navId };
    }

    /**
     * Routes to the content page corresponding "route" for the given document info
     * Even though the component loading is done by the loadedDocument's observable this gives the user
     * a bookmarkable address bar link
     * @param documentInfo Document info to display in the url bar
     * @param additionalParams Additional params other than the standard file, drive, item query params
     */
    private routeToContentPage(documentInfo: DocumentInfo, additionalParams?: Params): Promise<boolean> {
        const params = WacContentService.getParamsForDocumentInfo(documentInfo, additionalParams);

        return this.officeRouteService.navigateTo(
            { route: OfficeRoute.Content, app: documentInfo.fileType },
            false,
            params
        );
    }

    private decodeParamWithKey(params: Params, key: string): string | undefined {
        const encodedValue = params[key];
        if (encodedValue) {
            let decodedValue = encodedValue;
            try {
                decodedValue = decodeURIComponent(encodedValue.replace(/\+/g, ' '));
            } catch {
                decodedValue = encodedValue ?? undefined;
            }
            return decodedValue;
        }

        return undefined;
    }

    private getContentPath(fileType: FileType): string {
        return `${this.capabilitiesService.capabilities.startPageRootPath}${FileType[fileType].toLowerCase()}${this.capabilitiesService.capabilities.wacHostPagePath}`;
    }

    private getQueryParamString(params: Params): string {
        return Object.keys(params).filter(key => params[key]).reduce((prev, key) => prev += `&${key}=${params[key]}`, '').slice(1);
    }

    /**
     * Parses the application name from the content route location
     * Working Example: "/launch/word/content?auth=2&drive=b!GyhB1tH9gEy2KNFUiHPM&item=015QQWMWZSY&file=https://documentUrl.com" would return 'word'
     * Undefined Example: "/?auth=2" would return undefined
     * Another undefined example: "/launch/word?auth=2" would return undefined
     *
     * @param urlLoc The location of the route to parse
     */
    private getAppNameFromUrl(urlLoc?: string): string {
        if (urlLoc) {
            const parsed = urlLoc.split('/');
            if (parsed.length >= 3) {
                return parsed[2];
            }
        }

        return undefined;
    }

    private setupAbandonListener() {
        this.getLoadedDocumentsObservable()
            .pipe(pairwise())
            .subscribe(([prev, cur]) => {
                prev?.forEach(async prevItem => {
                    if (!cur?.some(curItem => prevItem.bootId === curItem.bootId)) {
                        const data = await prevItem.data.pipe(first()).toPromise();
                        if (!data.bootCompleted) {
                            this.logAbandon(data.bootAndLoadInfo);
                        }
                    }
                });
            });
    }

    private setupResetShellListener() {
        this.hasLoadedDocumentsObservable().pipe(distinctUntilChanged(), skip(1)).subscribe(hasDocs => {
            if (!hasDocs) {
                this.resetShell();
            }
        });
    }

    private logLoadStart(bootInfo: BootAndLoadInfo) {
        this.instrumentationService.logGenericEvent(
            KnownInstrumentationEvents.WacDocumentLoadStart,
            createWacContentInstrumentationParams(bootInfo.correlationId, bootInfo.bootedDocumentInfo.documentInfo)
        );
    }

    private logAbandon(bootInfo: BootAndLoadInfo) {
        this.instrumentationService.logGenericEvent(
            KnownInstrumentationEvents.WacDocumentAbandon,
            createWacContentInstrumentationParams(bootInfo.correlationId, bootInfo.bootedDocumentInfo.documentInfo)
        );
    }
}
